import { useRequiredContext } from "@redotech/react-util/context";
import QuestionCircleIcon from "@redotech/redo-icons/help-circle.svg";
import TicketIcon from "@redotech/redo-icons/ticket-02.svg";
import TicketFilledIcon from "@redotech/redo-icons/ticket-02_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { ConversationCountsContext } from "@redotech/redo-merchant-support-app/services/conversation-counts-service";
import { SupportViewOrderingContext } from "@redotech/redo-merchant-support-app/services/support-view-ordering-service";
import { ViewInfo } from "@redotech/redo-merchant-support-app/views";
import {
  SettingsPermissions,
  SupportPermissions,
} from "@redotech/redo-model/permissions";
import type { SupportFolder as SupportFolderType } from "@redotech/redo-model/support-folder";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import {
  RedoNavDraggableList,
  RedoNavDraggableListItem,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-nav-draggable-list";
import { RedoNavFolder } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-nav-folder";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useMemo } from "react";
import { useLocation, useMatch } from "react-router-dom";
import {
  SUPPORT_ICON,
  SUPPORT_ICON_FILLED,
  SupportSuiteStatusesContext,
} from "../../../../redo-suite/support-cloud";
import { SupportAnalyticsItems } from "./analytics";
import { ConciergeItems } from "./concierge";
import { DevItems } from "./dev";
import { useSupportNotifications } from "./support-notifications";

export function useSupportNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { supportEnabled } = useRequiredContext(SupportSuiteStatusesContext);

  const canViewSupport = useHasPermission(SupportPermissions.VIEW_SUPPORT);

  const conversationCounts = useRequiredContext(ConversationCountsContext);

  useSupportNotifications();

  const hasAnyNonZeroCounts = useMemo(() => {
    return Object.values(conversationCounts).some((count) => count > 0);
  }, [conversationCounts]);

  if (!canViewSupport) {
    return null;
  }

  const baseUrl = routes.supportBaseUrl;
  const primaryUrl = routes.getSupportHref({ enabled: supportEnabled });

  const primaryTab: LinkPrimaryTab = {
    id: "support",
    hasNotification: hasAnyNonZeroCounts,
    UnfilledIcon: SUPPORT_ICON,
    FilledIcon: SUPPORT_ICON_FILLED,
    type: "link",
    pathRoot: baseUrl,
    href: primaryUrl,
    friendlyName: "Support",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "support",
    name: "Support",
    NavContent: SecondarySupportSection,
  };

  return { primaryTab, secondaryNavigationData };
}

const SecondarySupportSection = memo(function SecondarySupportSection() {
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: "Support",
  });

  return (
    <TreeContext.Provider value={nodeId}>
      <ConciergeItems />
      <SupportTicketsItem />
      <SupportAnalyticsItems />
      <FaqItems />
      <DevItems />
    </TreeContext.Provider>
  );
});

const SupportTicketsItem = memo(function SupportTicketsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { supportEnabled } = useRequiredContext(SupportSuiteStatusesContext);
  const conversationCounts = useRequiredContext(ConversationCountsContext);

  const {
    folderlessViewOrdering,
    setFolderBeingEdited,
    folders,
    updateViewOrdering,
    openCreateFolderModal,
  } = useRequiredContext(SupportViewOrderingContext);

  const reorderableViews: RedoNavDraggableListItem<ViewInfo>[] = useMemo(() => {
    const itemToProps = (
      view: ViewInfo,
    ): RedoNavDraggableListItem<ViewInfo> => {
      const viewNameToUse = view.identifier.name || view.title;
      const viewCountKey = view.identifier?._id
        ? view.title
        : view.identifier?.name || "";

      const unreadCount = conversationCounts[viewCountKey] ?? 0;

      return {
        id: view.identifier._id || view.identifier.name || "",
        friendlyName: view.title,
        notificationCount: unreadCount,
        itemValue: view,
        isPrivate: view.isPrivate,
        action: {
          type: "link",
          href: routes.getSupportTableHref(viewNameToUse),
        },
      };
    };

    return folderlessViewOrdering.map(itemToProps);
  }, [conversationCounts, folderlessViewOrdering, routes]);

  const supportBaseUrl = routes.supportBaseUrl;
  const anyChildSelected = useMatch(`${supportBaseUrl}/*`);

  if (!supportEnabled) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={TicketFilledIcon}
      friendlyName="Tickets"
      id="tickets"
      UnfilledIcon={TicketIcon}
    >
      {folders.map((folder) => (
        <SupportFolder
          folder={folder}
          folderEditRequested={() => setFolderBeingEdited(folder)}
          key={folder._id}
        />
      ))}
      <RedoNavDraggableList
        draggableItems={reorderableViews}
        setDraggableItems={(newOrder) =>
          updateViewOrdering(newOrder.map((item) => item.itemValue))
        }
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.getSupportTableSubRoute("sent") }}
        friendlyName="Sent"
        id="view-sent"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.getSupportTableSubRoute("spam") }}
        friendlyName="Spam"
        id="view-spam"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getSupportTableSubRoute("create"),
        }}
        friendlyName="+ Add new view"
        id="create-view"
      />
      <RedoSecondaryNavItem
        action={{ type: "button", onClick: openCreateFolderModal }}
        friendlyName="+ Create folder"
        id="create-folder"
      />
    </RedoSecondaryNavItem>
  );
});

const SupportFolder = memo(function SupportFolder({
  folder,
  folderEditRequested,
}: {
  folder: SupportFolderType;
  folderEditRequested: () => void;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  const { defaultViewOrdering } = useRequiredContext(
    SupportViewOrderingContext,
  );

  const conversationCounts = useRequiredContext(ConversationCountsContext);

  const userDefinedViewsInFolder = useMemo(() => {
    const viewIds = new Set(folder.viewIds);

    return defaultViewOrdering.filter((view) =>
      view.identifier._id ? viewIds.has(view.identifier._id) : false,
    );
  }, [folder.viewIds, defaultViewOrdering]);

  const defaultViewsInFolder = useMemo(() => {
    const viewNames = new Set(folder.defaultViews);

    return defaultViewOrdering.filter((view) =>
      view.identifier.name ? viewNames.has(view.identifier.name) : false,
    );
  }, [folder.defaultViews, defaultViewOrdering]);

  const allFolderViews = useMemo(() => {
    return [...userDefinedViewsInFolder, ...defaultViewsInFolder];
  }, [userDefinedViewsInFolder, defaultViewsInFolder]);

  const location = useLocation();

  const anyChildSelected = useMemo(() => {
    return allFolderViews.some((view) => {
      const viewNameToUse = view.identifier.name || view.title;
      const link = routes.getSupportTableHref(viewNameToUse);
      return location.pathname.startsWith(link);
    });
  }, [location.pathname, allFolderViews, routes]);

  const folderChildren = allFolderViews.map((view) => {
    const uniqueId = view.identifier._id || view.identifier.name || "";

    const linkName = view.identifier.name || view.title;

    const link = routes.getSupportTableHref(linkName);

    const viewCountKey = view.identifier?._id
      ? view.title
      : view.identifier?.name || "";

    const unreadCount = conversationCounts[viewCountKey] ?? 0;

    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href: link }}
        friendlyName={view.title}
        id={uniqueId}
        isPrivate={view.isPrivate}
        key={uniqueId}
        notificationCount={unreadCount}
      />
    );
  });

  return (
    <RedoNavFolder
      anyChildSelected={anyChildSelected}
      folderEditRequested={folderEditRequested}
      folderId={folder._id}
      folderName={folder.name}
    >
      {folderChildren}
    </RedoNavFolder>
  );
});

const FaqItems = memo(function FaqItems() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const canManageFaq = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );

  const supportBaseUrl = routes.supportBaseUrl;
  const anyChildSelected = useMatch(`${supportBaseUrl}/faq/*`);

  if (!canManageFaq) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={QuestionCircleIcon}
      friendlyName="FAQ"
      id="faq"
      UnfilledIcon={QuestionCircleIcon}
    >
      <RedoSecondaryNavItem
        action={{ type: "link", href: `${supportBaseUrl}/faq/home` }}
        friendlyName="Content"
        id="faq-collections-and-articles"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: `${supportBaseUrl}/faq/editor/theme` }}
        friendlyName="Style"
        id="faq-theme-editor"
      />
    </RedoSecondaryNavItem>
  );
});
